/**
 * ImportWorkspaceDialog - Dialog para importar workspace CRM
 * 
 * Layout 2 colunas idêntico ao ImportFlowDialog:
 * sidebar escura com steps + conteúdo scrollável à direita.
 * Inclui wizard de mapeamento de referências para fluxos (Fase 3).
 * 
 * RELEASE: 1.0.0 - 2026-03-22 - Fase 2: Importação
 * RELEASE: 2.0.0 - 2026-03-22 - Fase 3: Wizard de mapeamento de fluxos
 * RELEASE: 3.0.0 - 2026-03-22 - Layout 2 colunas (padrão ImportFlowDialog)
 * 
 * @author Sistema
 * @version 3.0.0
 */

import { LegacyTransferNotice } from '@/components/template-system/LegacyTransferNotice';
import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Upload,
  Package,
  GitBranch,
  ShoppingBag,
  Tag,
  DollarSign,
  FileText,
  Calendar,
  Zap,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  FileUp,
  Check,
  ChevronLeft,
  ChevronRight,
  Settings,
  Layers,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import {
  parseWorkspaceFile,
  importWorkspace,
  type ImportProgress,
  type ImportResult,
} from '@/lib/workspace/workspace-import';
import type {
  WorkspaceExportFormat,
  WorkspaceExportModules,
} from '@/lib/workspace/workspace-types';
import type { InternalReferenceMaps } from './ImportWorkspaceFlowMapping';
import {
  autoMatchReferences,
  importFlow,
} from '@/lib/flows/flow-import';
import {
  REFERENCE_TYPE_CONFIGS,
  type ExternalReference,
  type ExternalReferenceType,
  type ReferenceMapping,
  type AutoMatchResult,
} from '@/lib/flows/reference-types';
import { ReferenceMappingContent } from '@/components/tenant/flows/import/ReferenceMappingWizard';
import type { LucideIcon } from 'lucide-react';
import { SellerFormDialog } from '@/components/crm/sellers/SellerFormDialog';
import { CreateDepartmentDialog } from '@/components/tenant/departments/CreateDepartmentDialog';
import { CreateInboxDialog } from '@/components/tenant/CreateInboxDialog';
import { CreateCredentialDialog } from '@/components/tenant/credentials/CreateCredentialDialog';
import { CreateMeioPagamentoDialog } from '@/components/financial/CreateMeioPagamentoDialog';

/** Tipos que suportam criação inline via modais existentes */
const CREATABLE_TYPES: ExternalReferenceType[] = ['seller', 'department', 'inbox', 'credential', 'meio_pagamento'];

/** Logger */
const logger = createLogger('ImportWorkspaceDialog');

/**
 * Configuração visual dos módulos importáveis
 */
const MODULE_CONFIG = [
  { key: 'pipelines' as const, label: 'Pipelines e Fases', icon: GitBranch, countFn: (d: WorkspaceExportFormat) => d.pipelines?.length || 0 },
  { key: 'product_categories' as const, label: 'Categorias de Produto', icon: Tag, countFn: (d: WorkspaceExportFormat) => d.product_categories?.length || 0 },
  { key: 'products' as const, label: 'Produtos', icon: ShoppingBag, countFn: (d: WorkspaceExportFormat) => d.products?.length || 0 },
  { key: 'price_tables' as const, label: 'Tabelas de Preço', icon: DollarSign, countFn: (d: WorkspaceExportFormat) => d.price_tables?.length || 0 },
  { key: 'contracts' as const, label: 'Contratos', icon: FileText, countFn: (d: WorkspaceExportFormat) => d.contracts?.length || 0 },
  { key: 'scheduling' as const, label: 'Agendamento Online', icon: Calendar, countFn: (d: WorkspaceExportFormat) => d.scheduling_event_types?.length || 0 },
  { key: 'flows' as const, label: 'Fluxos', icon: Zap, countFn: (d: WorkspaceExportFormat) => d.flows?.length || 0 },
  { key: 'conversation_kanban' as const, label: 'Kanban de Conversas', icon: Layers, countFn: (d: WorkspaceExportFormat) => d.kanban_categories?.length || 0, description: 'Independente do workspace' },
] as const;

/** Etapas do wizard */
type WizardStep = 'upload' | 'preview' | 'importing' | 'flow_mapping' | 'done';

/** Configuração de steps para sidebar */
interface StepConfig {
  key: WizardStep;
  label: string;
  icon: LucideIcon;
  description: string;
}

const STEPS: StepConfig[] = [
  { key: 'upload', label: 'Upload', icon: Upload, description: 'Selecione o arquivo .uaizyworkspace' },
  { key: 'preview', label: 'Configuração', icon: Settings, description: 'Revise o conteúdo e selecione os módulos' },
  { key: 'importing', label: 'Importando', icon: Loader2, description: 'Criando entidades no sistema...' },
  { key: 'flow_mapping', label: 'Fluxos', icon: Zap, description: 'Mapeie referências externas dos fluxos' },
  { key: 'done', label: 'Concluído', icon: CheckCircle2, description: 'Resultado da importação' },
];

/** Tipos internos de referência auto-mapeáveis */
const INTERNAL_REF_TYPES: ExternalReferenceType[] = ['workspace', 'pipeline', 'phase', 'crm_product'];

interface ImportWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImportWorkspaceDialog({ open, onOpenChange }: ImportWorkspaceDialogProps) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // =========================================================================
  // Estados do wizard
  // =========================================================================
  const [step, setStep] = useState<WizardStep>('upload');
  const [fileData, setFileData] = useState<WorkspaceExportFormat | null>(null);
  const [fileName, setFileName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [parseErrors, setParseErrors] = useState<string[]>([]);

  // Módulos selecionados
  const [modules, setModules] = useState<WorkspaceExportModules>({
    pipelines: true,
    product_categories: true,
    products: true,
    price_tables: true,
    contracts: true,
    scheduling: true,
    flows: true,
    conversation_kanban: false,
  });

  // Progresso da importação
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  // Mapas internos para mapeamento de fluxos (Fase 3)
  const [internalMaps, setInternalMaps] = useState<InternalReferenceMaps | null>(null);

  // =========================================================================
  // Estados do mapeamento de fluxos (embutido)
  // =========================================================================
  const [currentFlowIndex, setCurrentFlowIndex] = useState(0);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [autoMatches, setAutoMatches] = useState<AutoMatchResult[]>([]);
  const [flowMappings, setFlowMappings] = useState<Map<string, string | null>>(new Map());
  const [currentMappingType, setCurrentMappingType] = useState(0);
  const [isImportingFlow, setIsImportingFlow] = useState(false);
  const [importedFlowCount, setImportedFlowCount] = useState(0);
  const [flowWarnings, setFlowWarnings] = useState<string[]>([]);
  const [autoCreateVariables, setAutoCreateVariables] = useState(true);

  // Estados dos modais de criação inline (botão "+")
  const [createDialogType, setCreateDialogType] = useState<ExternalReferenceType | null>(null);

  // Validação de nome duplicado de workspace
  const [duplicateName, setDuplicateName] = useState(false);
  const [checkingName, setCheckingName] = useState(false);

  // Efeito com debounce para verificar nome duplicado
  useEffect(() => {
    if (!workspaceName.trim() || !currentTenant?.id || step !== 'preview') {
      setDuplicateName(false);
      return;
    }
    setCheckingName(true);
    const timer = setTimeout(async () => {
      const { count } = await supabase
        .from('crm_workspaces')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', currentTenant.id)
        .ilike('name', workspaceName.trim());
      setDuplicateName((count || 0) > 0);
      setCheckingName(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [workspaceName, currentTenant?.id, step]);
  // =========================================================================
  // Computações derivadas
  // =========================================================================

  /** Step index para a sidebar */
  const stepIndex = STEPS.findIndex(s => s.key === step);
  const currentStepConfig = STEPS[stepIndex] || STEPS[0];
  const StepIcon = currentStepConfig.icon;
  const totalSteps = STEPS.length;

  /** Fluxo atual no mapeamento */
  const currentFlow = step === 'flow_mapping' && fileData?.flows ? fileData.flows[currentFlowIndex] : null;

  /** Conta módulos selecionados com dados */
  const selectedModuleCount = fileData
    ? Object.entries(modules).filter(([key, val]) => {
        if (!val) return false;
        const config = MODULE_CONFIG.find(m => m.key === key);
        return config && config.countFn(fileData) > 0;
      }).length
    : 0;

  /** Verifica se há pelo menos um módulo selecionado com dados */
  const hasValidSelection = fileData && selectedModuleCount > 0;

  // =========================================================================
  // Mapeamento de fluxos — lógica extraída do ImportWorkspaceFlowMapping
  // =========================================================================

  /** Extrai referências externas do fluxo atual (separando internas) */
  const { externalReferences, internalMappingsForFlow } = useMemo(() => {
    if (!currentFlow || !internalMaps) return { externalReferences: [] as ExternalReference[], internalMappingsForFlow: new Map<string, string>() };

    const rawRefs: ExternalReference[] = (currentFlow.external_references || [])
      .filter(r => r.placeholder && r.type)
      .map(r => ({
        placeholder: r.placeholder,
        type: r.type as ExternalReferenceType,
        originalId: r.originalId,
        name: r.name,
        context: r.context,
        nodeId: r.nodeId,
        nodeType: r.nodeType,
      }));

    // Deduplicar por placeholder
    const unique: ExternalReference[] = [];
    const seen = new Set<string>();
    for (const ref of rawRefs) {
      if (!seen.has(ref.placeholder)) {
        seen.add(ref.placeholder);
        unique.push(ref);
      }
    }

    // Separar internas (auto-mapeáveis) das externas
    const autoMap = new Map<string, string>();
    const external: ExternalReference[] = [];

    for (const ref of unique) {
      let resolved = false;

      if (ref.type === 'workspace' && ref.name === internalMaps.workspaceName) {
        autoMap.set(ref.placeholder, internalMaps.workspaceId);
        resolved = true;
      }
      if (ref.type === 'pipeline' && ref.name && internalMaps.pipelines[ref.name]) {
        autoMap.set(ref.placeholder, internalMaps.pipelines[ref.name]);
        resolved = true;
      }
      if (ref.type === 'phase' && ref.name) {
        for (const [key, id] of Object.entries(internalMaps.phases)) {
          const phaseName = key.split('::')[1];
          if (phaseName === ref.name) {
            autoMap.set(ref.placeholder, id);
            resolved = true;
            break;
          }
        }
      }
      if (ref.type === 'crm_product' && ref.name && internalMaps.products[ref.name]) {
        autoMap.set(ref.placeholder, internalMaps.products[ref.name]);
        resolved = true;
      }

      if (!resolved) external.push(ref);
    }

    return { externalReferences: external, internalMappingsForFlow: autoMap };
  }, [currentFlow, internalMaps]);

  const hasExternalRefs = externalReferences.length > 0;

  /** Agrupa referências por tipo */
  const referencesByType = useMemo(() => {
    const grouped = new Map<ExternalReferenceType, ExternalReference[]>();
    for (const ref of externalReferences) {
      if (!grouped.has(ref.type)) grouped.set(ref.type, []);
      grouped.get(ref.type)!.push(ref);
    }
    return grouped;
  }, [externalReferences]);

  const mappingTypes = useMemo(() => Array.from(referencesByType.keys()), [referencesByType]);

  /** Estatísticas de mapeamento do fluxo atual */
  const flowMappingStats = useMemo(() => {
    let mapped = 0, required = 0, requiredMapped = 0;
    for (const ref of externalReferences) {
      const config = REFERENCE_TYPE_CONFIGS[ref.type];
      const isMapped = flowMappings.has(ref.placeholder);
      if (isMapped) mapped++;
      if (config?.required) {
        required++;
        if (isMapped) requiredMapped++;
      }
    }
    return { total: externalReferences.length, mapped, required, requiredMapped, canComplete: requiredMapped === required };
  }, [externalReferences, flowMappings]);

  const flowMappingProgress = flowMappingStats.total > 0 ? (flowMappingStats.mapped / flowMappingStats.total) * 100 : 0;

  /** Carrega auto-matches ao mudar de fluxo */
  useEffect(() => {
    if (step !== 'flow_mapping' || !currentFlow || !currentTenant?.id) return;

    if (externalReferences.length === 0) {
      setIsLoadingMatches(false);
      setAutoMatches([]);
      setFlowMappings(new Map());
      return;
    }

    setIsLoadingMatches(true);
    setFlowMappings(new Map());
    setCurrentMappingType(0);

    autoMatchReferences(externalReferences, currentTenant.id)
      .then(matches => {
        setAutoMatches(matches);
        const initial = new Map<string, string | null>();
        for (const match of matches) {
          const exact = match.candidates.find(c => c.similarity === 1);
          if (exact) initial.set(match.reference.placeholder, exact.id);
        }
        setFlowMappings(initial);
      })
      .catch(err => logger.error('Erro ao carregar auto-matches', err))
      .finally(() => setIsLoadingMatches(false));
  }, [step, currentFlowIndex, externalReferences, currentTenant?.id, currentFlow]);

  // =========================================================================
  // Handlers
  // =========================================================================

  /** Reseta todos os estados */
  const resetDialog = useCallback(() => {
    setStep('upload');
    setFileData(null);
    setFileName('');
    setWorkspaceName('');
    setParseErrors([]);
    setModules({
      pipelines: true, product_categories: true, products: true,
      price_tables: true, contracts: true, scheduling: true, flows: true,
      conversation_kanban: false,
    });
    setProgress(null);
    setResult(null);
    setInternalMaps(null);
    setCurrentFlowIndex(0);
    setIsLoadingMatches(false);
    setAutoMatches([]);
    setFlowMappings(new Map());
    setCurrentMappingType(0);
    setIsImportingFlow(false);
    setImportedFlowCount(0);
    setFlowWarnings([]);
    setAutoCreateVariables(true);
    setCreateDialogType(null);
  }, []);

  /** Handler de upload de arquivo */
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const validation = await parseWorkspaceFile(file);

    if (!validation.valid || !validation.data) {
      setParseErrors(validation.errors);
      toast.error('Arquivo inválido');
      e.target.value = '';
      return;
    }

    setFileData(validation.data);
    setWorkspaceName(validation.data.workspace.name);
    setParseErrors([]);

    // Auto-selecionar módulos que existem no arquivo
    const autoModules: WorkspaceExportModules = {
      pipelines: (validation.data.pipelines?.length || 0) > 0,
      product_categories: (validation.data.product_categories?.length || 0) > 0,
      products: (validation.data.products?.length || 0) > 0,
      price_tables: (validation.data.price_tables?.length || 0) > 0,
      contracts: (validation.data.contracts?.length || 0) > 0,
      scheduling: (validation.data.scheduling_event_types?.length || 0) > 0,
      flows: (validation.data.flows?.length || 0) > 0,
      conversation_kanban: (validation.data.kanban_categories?.length || 0) > 0,
    };
    setModules(autoModules);
    setStep('preview');
    e.target.value = '';
  };

  /** Alterna seleção de módulo */
  const toggleModule = (key: keyof WorkspaceExportModules) => {
    setModules(prev => ({ ...prev, [key]: !prev[key] }));
  };

  /** Executa a importação (workspace + entidades, sem fluxos) */
  const handleImport = async () => {
    if (!currentTenant?.id || !fileData || !workspaceName.trim()) {
      toast.error('Preencha o nome do workspace');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      toast.error('Sessão expirada. Faça login novamente.');
      return;
    }

    setStep('importing');

    const modulesWithoutFlows: WorkspaceExportModules = { ...modules, flows: false };
    const importResult = await importWorkspace(
      fileData, modulesWithoutFlows, workspaceName,
      currentTenant.id, user.id, setProgress,
    );

    setResult(importResult);

    if (importResult.success) {
      queryClient.invalidateQueries({ queryKey: ['crm-workspaces-stats'] });
      queryClient.invalidateQueries({ queryKey: ['workspace-pipelines'] });
    }

    const hasFlowsToImport = modules.flows && (fileData.flows?.length || 0) > 0;
    if (importResult.success && hasFlowsToImport && importResult.internalMaps) {
      setInternalMaps(importResult.internalMaps);
      setCurrentFlowIndex(0);
      setStep('flow_mapping');
      toast.success('Workspace criado! Agora configure os fluxos.');
    } else {
      setStep('done');
      if (importResult.success) {
        toast.success('Workspace importado com sucesso!');
      } else {
        toast.error('Erro na importação do workspace');
      }
    }
  };

  /** Handler para mudança de mapeamento de fluxo */
  const handleFlowMappingChange = useCallback((placeholder: string, mappedId: string | null) => {
    setFlowMappings(prev => {
      const next = new Map(prev);
      if (mappedId === null) next.delete(placeholder);
      else next.set(placeholder, mappedId);
      return next;
    });
  }, []);


  /**
   * Abre o modal de criação para o tipo de entidade especificado
   * Chamado pelo botão "+" no ReferenceMappingItem
   */
  const handleCreateForType = useCallback((type: ExternalReferenceType) => {
    if (CREATABLE_TYPES.includes(type)) {
      setCreateDialogType(type);
    }
  }, []);

  /**
   * Callback de sucesso após criar entidade inline
   * Recarrega auto-matches para atualizar candidatos disponíveis
   */
  const handleCreateSuccess = useCallback(async () => {
    setCreateDialogType(null);
    if (!currentTenant?.id || !currentFlow) return;

    // Recarregar auto-matches para o fluxo atual
    try {
      const matches = await autoMatchReferences(externalReferences, currentTenant.id);
      setAutoMatches(matches);

      // Reaplicar matches exatos
      const updated = new Map(flowMappings);
      for (const match of matches) {
        const exact = match.candidates.find(c => c.similarity === 1);
        if (exact && !updated.has(match.reference.placeholder)) {
          updated.set(match.reference.placeholder, exact.id);
        }
      }
      setFlowMappings(updated);
      logger.info('Candidatos atualizados após criação inline');
    } catch (err) {
      logger.error('Erro ao recarregar candidatos', err);
    }
  }, [currentTenant?.id, currentFlow, externalReferences, flowMappings]);

  /** Importa o fluxo atual e avança para o próximo */
  const handleImportCurrentFlow = async () => {
    if (!currentFlow || !currentTenant?.id) return;

    setIsImportingFlow(true);
    try {
      const allRefs: ExternalReference[] = (currentFlow.external_references || [])
        .filter(r => r.placeholder && r.type)
        .map(r => ({
          placeholder: r.placeholder,
          type: r.type as ExternalReferenceType,
          originalId: r.originalId,
          name: r.name,
          context: r.context,
          nodeId: r.nodeId,
          nodeType: r.nodeType,
        }));

      const allMappings: ReferenceMapping[] = allRefs.map(ref => {
        const userMapping = flowMappings.get(ref.placeholder);
        const internalMapping = internalMappingsForFlow.get(ref.placeholder);
        const mappedId = userMapping ?? internalMapping ?? null;
        return { placeholder: ref.placeholder, mappedId, mappedName: undefined, autoMatched: !!internalMapping };
      });

      const flowResult = await importFlow(
        currentFlow,
        currentTenant.id,
        allMappings,
        `${currentFlow.flow.name} (importado via workspace)`,
        { autoCreateVariables },
      );

      if (flowResult.success) {
        setImportedFlowCount(prev => prev + 1);
      } else {
        setFlowWarnings(prev => [...prev, `Erro ao importar fluxo "${currentFlow.flow.name}": ${flowResult.error}`]);
      }

      // Avançar ou finalizar
      if (currentFlowIndex < (fileData?.flows?.length || 1) - 1) {
        setCurrentFlowIndex(prev => prev + 1);
      } else {
        finishFlowImport(importedFlowCount + (flowResult.success ? 1 : 0), flowWarnings);
      }
    } catch (err: any) {
      logger.error('Erro fatal ao importar fluxo', err);
      setFlowWarnings(prev => [...prev, `Erro fatal no fluxo "${currentFlow.flow.name}": ${err.message}`]);
      if (currentFlowIndex < (fileData?.flows?.length || 1) - 1) {
        setCurrentFlowIndex(prev => prev + 1);
      } else {
        finishFlowImport(importedFlowCount, [...flowWarnings, err.message]);
      }
    } finally {
      setIsImportingFlow(false);
    }
  };

  /** Pula o fluxo atual */
  const handleSkipFlow = () => {
    const name = currentFlow?.flow?.name || 'Desconhecido';
    setFlowWarnings(prev => [...prev, `Fluxo "${name}" pulado pelo usuário.`]);
    if (currentFlowIndex < (fileData?.flows?.length || 1) - 1) {
      setCurrentFlowIndex(prev => prev + 1);
    } else {
      finishFlowImport(importedFlowCount, [...flowWarnings, `Fluxo "${name}" pulado.`]);
    }
  };

  /** Cancela importação de fluxos restantes */
  const handleCancelFlows = () => {
    finishFlowImport(importedFlowCount, [...flowWarnings, 'Importação de fluxos cancelada pelo usuário.']);
  };

  /** Finaliza importação de fluxos e vai para resultado */
  const finishFlowImport = (count: number, warns: string[]) => {
    if (result) {
      setResult({
        ...result,
        summary: { ...result.summary, flows: count },
        warnings: [...result.warnings, ...warns],
      });
    }
    queryClient.invalidateQueries({ queryKey: ['flows'] });
    setStep('done');
    if (count > 0) toast.success(`${count} fluxo(s) importado(s)!`);
  };

  /** Fecha o dialog */
  const handleClose = (open: boolean) => {
    if (!open) {
      if (step === 'importing') return;
      resetDialog();
    }
    onOpenChange(open);
  };

  // =========================================================================
  // Renderização de conteúdo por step
  // =========================================================================

  const renderStepContent = () => {
    switch (step) {
      // ===== UPLOAD =====
      case 'upload':
        return (
          <div className="space-y-4">
            <label
              htmlFor="workspace-file"
              className="flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10">
                <FileUp className="h-7 w-7 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-medium">Arraste um arquivo ou clique para selecionar</p>
                <p className="text-sm text-muted-foreground mt-1">Formato aceito: .uaizyworkspace</p>
              </div>
              <input
                id="workspace-file"
                type="file"
                accept=".uaizyworkspace"
                className="hidden"
                onChange={handleFileUpload}
              />
            </label>

            {parseErrors.length > 0 && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 space-y-1">
                {parseErrors.map((err, i) => (
                  <p key={i} className="text-sm text-destructive flex items-start gap-2">
                    <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    {err}
                  </p>
                ))}
              </div>
            )}
          </div>
        );

      // ===== PREVIEW / CONFIGURAÇÃO =====
      case 'preview':
        if (!fileData) return null;
        return (
          <div className="space-y-4">
            {/* Nome do workspace */}
            <div className="space-y-2">
              <Label htmlFor="ws-name">Nome do Workspace</Label>
              <Input
                id="ws-name"
                value={workspaceName}
                onChange={e => setWorkspaceName(e.target.value)}
                placeholder="Nome do workspace..."
                className={cn(duplicateName && 'border-destructive focus-visible:ring-destructive')}
              />
              {duplicateName && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Já existe um workspace com este nome
                </p>
              )}
              {checkingName && (
                <p className="text-xs text-muted-foreground">Verificando nome...</p>
              )}
            </div>

            {/* Info de exportação */}
            <div className="p-2.5 rounded-lg bg-muted/50 text-xs text-muted-foreground space-y-1">
              <p>📅 Exportado em: {new Date(fileData.export_date).toLocaleString('pt-BR')}</p>
              <p>📂 Categoria: {fileData.workspace.category_slug}</p>
            </div>

            <Separator />

            {/* Módulos para importar */}
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Módulos para importar</Label>
              <div className="space-y-2">
                {MODULE_CONFIG.map(mod => {
                  const count = mod.countFn(fileData);
                  const isExported = fileData.exported_modules[mod.key];
                  const isDisabled = count === 0 || !isExported;
                  const Icon = mod.icon;

                  return (
                    <div
                      key={mod.key}
                      className={cn(
                        'flex items-center gap-3 p-2.5 rounded-lg border transition-colors',
                        modules[mod.key] && !isDisabled ? 'border-primary/30 bg-primary/5' : 'border-transparent',
                        isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50',
                      )}
                      onClick={() => !isDisabled && toggleModule(mod.key)}
                    >
                      <Checkbox
                        checked={modules[mod.key] && !isDisabled}
                        onCheckedChange={() => !isDisabled && toggleModule(mod.key)}
                        disabled={isDisabled}
                      />
                      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium flex-1">{mod.label}</span>
                      <Badge variant={count > 0 ? 'secondary' : 'outline'} className="text-xs">
                        {count}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator />

            {/* Notas */}
            <div className="text-xs text-muted-foreground space-y-1">
              <p>• Entidades serão criadas no tenant atual</p>
              <p>• Fluxos serão configurados na próxima etapa</p>
            </div>
          </div>
        );

      // ===== IMPORTANDO =====
      case 'importing':
        return (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            {progress ? (
              <div className="w-full max-w-xs space-y-3 text-center">
                <p className="font-medium text-sm">{progress.currentStep}</p>
                <Progress value={(progress.stepNumber / progress.totalSteps) * 100} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  Passo {progress.stepNumber} de {progress.totalSteps}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Iniciando importação...</p>
            )}
          </div>
        );

      // ===== MAPEAMENTO DE FLUXOS =====
      case 'flow_mapping':
        if (!currentFlow || !internalMaps) return null;

        return (
          <div className="flex flex-col gap-4">
            {/* Header do fluxo atual */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">{currentFlow.flow.name}</span>
              </div>
              <Badge variant="secondary" className="text-xs">
                Fluxo {currentFlowIndex + 1} de {fileData?.flows?.length || 0}
              </Badge>
            </div>

            {/* Info de mapeamentos internos automáticos */}
            {internalMappingsForFlow.size > 0 && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/50 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span>
                  {internalMappingsForFlow.size} referência(s) interna(s) mapeada(s) automaticamente
                </span>
              </div>
            )}

            <Separator />

            {/* Conteúdo de mapeamento */}
            {isLoadingMatches ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Analisando referências do fluxo...</p>
              </div>
            ) : !hasExternalRefs ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
                <p className="text-sm text-muted-foreground">
                  Todas as referências foram resolvidas automaticamente.
                </p>
              </div>
            ) : (
              <ReferenceMappingContent
                references={externalReferences}
                referencesByType={referencesByType}
                mappingTypes={mappingTypes}
                currentMappingType={currentMappingType}
                onMappingTypeChange={setCurrentMappingType}
                mappings={flowMappings}
                onMappingChange={handleFlowMappingChange}
                autoMatches={autoMatches}
                mappingStats={flowMappingStats}
                mappingProgress={flowMappingProgress}
                autoCreateVariables={autoCreateVariables}
                onAutoCreateVariablesChange={setAutoCreateVariables}
                onCreateForType={handleCreateForType}
              />
            )}
          </div>
        );

      // ===== RESULTADO =====
      case 'done':
        if (!result) return null;
        return (
          <div className="space-y-4">
            <div className={cn(
              'flex items-center gap-3 p-3 rounded-lg',
              result.success ? 'bg-primary/5 border border-primary/20' : 'bg-destructive/10 border border-destructive/20',
            )}>
              {result.success ? (
                <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 text-destructive shrink-0" />
              )}
              <span className="text-sm font-medium">
                {result.success ? 'Workspace importado com sucesso!' : 'Importação falhou'}
              </span>
            </div>

            {result.success && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                {result.summary.pipelines > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <GitBranch className="h-3.5 w-3.5" /> {result.summary.pipelines} pipeline(s)
                  </div>
                )}
                {result.summary.phases > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle2 className="h-3.5 w-3.5" /> {result.summary.phases} fase(s)
                  </div>
                )}
                {result.summary.product_categories > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Tag className="h-3.5 w-3.5" /> {result.summary.product_categories} categoria(s)
                  </div>
                )}
                {result.summary.products > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <ShoppingBag className="h-3.5 w-3.5" /> {result.summary.products} produto(s)
                  </div>
                )}
                {result.summary.price_tables > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <DollarSign className="h-3.5 w-3.5" /> {result.summary.price_tables} tabela(s) de preço
                  </div>
                )}
                {result.summary.contracts > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" /> {result.summary.contracts} contrato(s)
                  </div>
                )}
                {result.summary.scheduling_event_types > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" /> {result.summary.scheduling_event_types} agendamento(s)
                  </div>
                )}
                {result.summary.flows > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Zap className="h-3.5 w-3.5" /> {result.summary.flows} fluxo(s)
                  </div>
                )}
              </div>
            )}

            {result.warnings.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Avisos ({result.warnings.length})</Label>
                <ScrollArea className="max-h-[120px]">
                  <div className="space-y-1 pr-2">
                    {result.warnings.map((w, i) => (
                      <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                        <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5 text-amber-500" />
                        {w}
                      </p>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // =========================================================================
  // Renderização Principal — Layout 2 colunas (padrão ImportFlowDialog)
  // =========================================================================

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] h-[90vh] p-0 overflow-hidden gap-0 flex flex-col"
        preventCloseOnOutsideInteraction={step === 'importing' || step === 'flow_mapping'}
      >
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-full min-h-0 flex-1">
          {/* ============================================================ */}
          {/* SIDEBAR - Navegação de Steps + Resumo */}
          {/* ============================================================ */}
          <aside
            className="hidden md:flex flex-col relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, hsl(210, 80%, 15%) 0%, hsl(210, 70%, 20%) 50%, hsl(200, 60%, 25%) 100%)'
            }}
          >
            {/* Elementos decorativos de fundo */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -left-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 right-4 w-32 h-32 bg-white/5 rounded-full blur-2xl" />
            </div>

            {/* Header da Sidebar */}
            <div className="relative z-10 p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-white">Importar Workspace</h2>
                  <p className="text-xs text-white/60">
                    {stepIndex + 1} de {totalSteps} etapas
                  </p>
                </div>
              </div>
            </div>

            {/* Lista de Steps */}
            <nav className="relative z-10 flex-1 p-4 space-y-1 overflow-y-auto">
              {STEPS.map((s, idx) => {
                const isActive = step === s.key;
                const isCompleted = stepIndex > idx;
                const Icon = s.icon;

                return (
                  <div
                    key={s.key}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all",
                      isActive && "bg-white/10 border border-white/20",
                      !isActive && !isCompleted && "text-white/50",
                    )}
                  >
                    {/* Círculo indicador */}
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center shrink-0",
                      "border-2 transition-colors",
                      isCompleted && "bg-green-500 border-green-500 text-white",
                      isActive && "bg-white border-white text-primary",
                      !isActive && !isCompleted && "border-white/30 text-white/50",
                    )}>
                      {isCompleted ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>

                    {/* Texto do step */}
                    <div className="flex-1 min-w-0">
                      <span className={cn(
                        "font-medium text-sm block truncate",
                        isActive && "text-white",
                        isCompleted && "text-white/70",
                        !isActive && !isCompleted && "text-white/50",
                      )}>
                        {s.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </nav>

            {/* Resumo em tempo real */}
            <div className="relative z-10 p-4 border-t border-white/10 bg-white/5">
              <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider mb-3">
                Resumo
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Arquivo</span>
                  <span className="font-medium text-white truncate max-w-[120px]">
                    {fileName || '-'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Módulos</span>
                  <span className="font-medium text-white">
                    {selectedModuleCount} de {MODULE_CONFIG.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Fluxos</span>
                  <span className="font-medium text-white">
                    {fileData?.flows?.length || 0}
                  </span>
                </div>
                {step === 'flow_mapping' && (
                  <div className="flex justify-between">
                    <span className="text-white/60">Fluxo atual</span>
                    <span className="font-medium text-white">
                      {currentFlowIndex + 1}/{fileData?.flows?.length || 0}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* ============================================================ */}
          {/* CONTEÚDO PRINCIPAL */}
          {/* ============================================================ */}
          <main className="flex flex-col h-full min-h-0 overflow-hidden">
            {/* Header do Step */}
            <DialogHeader className="flex-shrink-0 px-6 py-5 border-b bg-background">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <StepIcon className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <DialogTitle className="text-lg font-semibold">
                    {currentStepConfig.label}
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {currentStepConfig.description}
                  </DialogDescription>
                </div>
                {/* Badge mobile mostrando progresso */}
                <Badge variant="outline" className="md:hidden">
                  {stepIndex + 1}/{totalSteps}
                </Badge>
              </div>

              {/* Stepper mobile */}
              <div className="flex items-center gap-1.5 mt-4 md:hidden">
                {STEPS.map((s, idx) => (
                  <div
                    key={s.key}
                    className={cn(
                      "h-1.5 flex-1 rounded-full transition-colors",
                      stepIndex > idx && "bg-green-500",
                      step === s.key && "bg-primary",
                      stepIndex < idx && "bg-muted",
                    )}
                  />
                ))}
              </div>
            </DialogHeader>

            {/* Aviso do período de compatibilidade do formato antigo (.uaizyworkspace) */}
            <div className="px-6 pt-4">
              <LegacyTransferNotice mode="import" />
            </div>



            {/* Área de Conteúdo scrollável */}
            {/* Container scrollável unificado para todos os steps */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              <div className="px-6 py-5">
                {renderStepContent()}
              </div>
            </div>

            {/* Footer com Navegação */}
            <div className="flex-shrink-0 px-6 py-4 border-t bg-background flex items-center gap-4">
              {/* Botão Voltar */}
              {step === 'preview' && (
                <Button variant="ghost" onClick={() => { resetDialog(); }} className="shrink-0 gap-1">
                  <ChevronLeft className="h-4 w-4" />
                  Voltar
                </Button>
              )}

              {step === 'flow_mapping' && (
                <Button variant="ghost" size="sm" onClick={handleSkipFlow} disabled={isImportingFlow} className="shrink-0">
                  Pular fluxo
                </Button>
              )}

              {/* Espaço central */}
              <span className="flex-1 text-center text-sm text-muted-foreground hidden sm:block">
                {step === 'flow_mapping'
                  ? `Fluxo ${currentFlowIndex + 1} de ${fileData?.flows?.length || 0}`
                  : `Etapa ${stepIndex + 1} de ${totalSteps}`
                }
              </span>

              {/* Botões de ação por step */}
              {step === 'upload' && (
                <Button variant="outline" onClick={() => handleClose(false)} className="shrink-0 ml-auto">
                  Cancelar
                </Button>
              )}

              {step === 'preview' && (
                <Button
                  onClick={handleImport}
                  disabled={!hasValidSelection || !workspaceName.trim() || duplicateName}
                  className="shrink-0 gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Importar
                </Button>
              )}

              {step === 'flow_mapping' && (
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" onClick={handleCancelFlows} disabled={isImportingFlow}>
                    Cancelar fluxos
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleImportCurrentFlow}
                    disabled={isImportingFlow || isLoadingMatches}
                  >
                    {isImportingFlow ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      'Importar fluxo'
                    )}
                  </Button>
                </div>
              )}

              {step === 'done' && (
                <Button onClick={() => handleClose(false)} className="shrink-0 ml-auto">
                  Fechar
                </Button>
              )}
            </div>
          </main>
        </div>
      </DialogContent>

      {/* ================================================================== */}
      {/* Modais de criação inline para entidades referenciadas */}
      {/* ================================================================== */}

      {/* Modal de criação de vendedor */}
      {createDialogType === 'seller' && (
        <SellerFormDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Modal de criação de departamento */}
      {createDialogType === 'department' && currentTenant?.id && (
        <CreateDepartmentDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          tenantId={currentTenant.id}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Modal de criação de inbox */}
      {createDialogType === 'inbox' && currentTenant?.id && (
        <CreateInboxDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          tenantId={currentTenant.id}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Modal de criação de credencial */}
      {createDialogType === 'credential' && currentTenant?.id && (
        <CreateCredentialDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          tenantId={currentTenant.id}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Modal de criação de meio de pagamento */}
      {createDialogType === 'meio_pagamento' && (
        <CreateMeioPagamentoDialog
          open={true}
          onOpenChange={(open) => { if (!open) setCreateDialogType(null); }}
          onSuccess={handleCreateSuccess}
        />
      )}
    </Dialog>
  );
}
